import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Menu, X, ChevronLeft, ChevronRight,
  Home, Folder, Database, Users, BarChart, Settings, Bell,
  LogOut, ChevronDown, UserCheck, CheckSquare,
  Calendar as CalendarIcon, FileText, Image as ImageIcon,
  Trophy, Building2, TrendingUp, CalendarRange,
} from 'lucide-react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-hot-toast';
import { useApiAuth } from '../../contexts/ApiAuthContext';
import { useActivityLog } from '../../contexts/ActivityLogContext';
import { BRAND_COLORS, SYSTEM_COLORS } from '../../constants/brandPalette';
import { USERS_PRIMARY_NAV_ITEMS } from '../../features/users/config/accessControl';
import logo3d from '../../assets/logos/3D-LOGO.png';
import backgroundImg from '../../assets/images/background.jpg';

// ── Constants ─────────────────────────────────────────────────────────────────

const SIDEBAR_EXPANDED  = 240;
const SIDEBAR_COLLAPSED = 60;
// Auto-collapse below this viewport width (lg tablets / small laptops)
const AUTO_COLLAPSE_BELOW = 1280;

const PAGE_TITLES = {
  '/':                'Dashboard',
  '/projects':        'Projects',
  '/tasks':           'My Tasks',
  '/team':            'Team',
  '/user-management': 'User Management',
  '/resources':       'Resources',
  '/marketing':       'Marketing',
  '/pr':              'Public Relations',
  '/graphics':        'Graphics',
  '/calendar':        'Calendar',
  '/reports':         'Reports',
  '/settings':        'Settings',
  '/competitions':    'Competitions',
  '/clients':         'Clients',
  '/events':          'Events',
  '/member-approvals':'Member Approvals',
  '/permissions':     'Permissions',
};

const getPageTitle = (pathname) => {
  if (pathname?.startsWith('/projects/')) return 'Project Details';
  return PAGE_TITLES[pathname] || 'Dashboard';
};

const getRoleLabel = (role) =>
  role === 'admin'
    ? 'Administrator'
    : role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Member';

const getInitials = (name = '') =>
  name.split(' ').filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'U';

// ── Nav groups ────────────────────────────────────────────────────────────────

const USERS_ICON_MAP = { 'user-management': Users, team: Users };

const buildNavGroups = (hasPermission, userRole) => {
  const usersItems = USERS_PRIMARY_NAV_ITEMS
    .filter(i => !i.permission || hasPermission(i.permission))
    .map(i => ({ icon: USERS_ICON_MAP[i.key] || Users, label: i.label, path: i.path }));

  const raw = [
    [
      { icon: Home,         label: 'Dashboard',    path: '/',             permission: 'dashboard.view' },
      { icon: Folder,       label: 'Projects',     path: '/projects',     permission: 'projects.view' },
      { icon: CheckSquare,  label: 'My Tasks',     path: '/tasks',        permission: 'tasks.view' },
    ],
    usersItems,
    [
      { icon: Database,       label: 'Resources', path: '/resources', permission: 'resources.view' },
      { icon: CalendarIcon,  label: 'Calendar',  path: '/calendar',  permission: 'calendar.view'  },
      { icon: CalendarRange, label: 'Events',    path: '/events',    permission: 'events.view'    },
    ],
    [
      { icon: TrendingUp,   label: 'Marketing',    path: '/marketing',    permission: 'marketing.view' },
      { icon: FileText,     label: 'PR',           path: '/pr',           permission: 'pr.view' },
      { icon: ImageIcon,    label: 'Graphics',     path: '/graphics',     permission: 'graphics.view' },
    ],
    [
      { icon: BarChart,     label: 'Reports',      path: '/reports',      permission: 'reports.view' },
      { icon: Trophy,       label: 'Competitions', path: '/competitions', permission: 'competitions.view' },
      ...(hasPermission('clients.view') && (userRole === 'admin' || userRole === 'supervisor')
        ? [{ icon: Building2, label: 'Clients', path: '/clients', permission: 'clients.view' }]
        : []),
    ],
  ];

  return raw
    .map(g => g.filter(item => !item.permission || hasPermission(item.permission)))
    .filter(g => g.length > 0);
};

// ── NavItem ───────────────────────────────────────────────────────────────────

const NavItem = ({ item, isCollapsed, onClick }) => {
  const { pathname } = useLocation();
  const isActive = item.path === '/'
    ? pathname === '/'
    : pathname === item.path || pathname.startsWith(item.path + '/');

  return (
    <Link
      to={item.path}
      onClick={onClick}
      title={isCollapsed ? item.label : undefined}
      className="flex items-center rounded-xl transition-all duration-150 select-none min-h-[40px]"
      style={{
        gap: isCollapsed ? 0 : 10,
        padding: isCollapsed ? '10px 0' : '10px 11px',
        justifyContent: isCollapsed ? 'center' : 'flex-start',
        backgroundColor: isActive ? 'rgba(255,255,255,0.14)' : 'transparent',
        color: isActive ? '#ffffff' : 'rgba(255,255,255,0.62)',
      }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.07)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      <item.icon
        size={20}
        className="flex-shrink-0"
        style={{ color: isActive ? '#fff' : 'rgba(255,255,255,0.65)' }}
      />
      {!isCollapsed && (
        <>
          <span className="text-[14px] font-[500] leading-6  min-w-0">
            {item.label}
          </span>
          {isActive && (
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.5)' }} />
          )}
        </>
      )}
    </Link>
  );
};

// ── Sidebar inner content ─────────────────────────────────────────────────────

const Sidebar = ({ isCollapsed, onNavigate }) => {
  const { hasPermission, userRole } = useApiAuth();
  const groups = buildNavGroups(hasPermission, userRole);

  return (
    <div className="flex flex-col h-full overflow-hidden">

      {/* Brand — always 56 px tall, matches topbar */}
      <div
        className="flex items-center flex-shrink-0 border-b h-14"
        style={{
          padding: isCollapsed ? '0 10px' : '0 14px',
          gap: isCollapsed ? 0 : 10,
          justifyContent: isCollapsed ? 'center' : 'flex-start',
          borderColor: 'rgba(255,255,255,0.08)',
        }}
      >
        <div
          className="flex-shrink-0 overflow-hidden rounded-md"
          style={{ width: 35, height: 35, backgroundColor: 'rgba(255,255,255,0.18)' }}
        >
          <img src={logo3d} alt="Logo" className="object-cover w-full h-full" />
        </div>
        {!isCollapsed && (
          <div className="min-w-0">
            <p className="text-[20px] font-bold leading-tight text-white tracking-wide truncate">
              A.M.A.R.M.S
            </p>
           
          </div>
        )}
      </div>

      {/* Scrollable nav */}
      <nav
        className="flex-1 min-h-0 overflow-y-auto sidebar-scrollbar"
        style={{ padding: '10px 6px 16px' }}
      >
        {groups.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && (
              <div
                className="mx-1 my-2"
                style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.07)' }}
              />
            )}
            <div className="space-y-0.5">
              {group.map(item => (
                <NavItem
                  key={item.path}
                  item={item}
                  isCollapsed={isCollapsed}
                  onClick={onNavigate}
                />
              ))}
            </div>
          </React.Fragment>
        ))}
      </nav>

      {/* Settings pinned at bottom */}
      <div
        className="flex-shrink-0 border-t"
        style={{ padding: '6px 6px 8px', borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <NavItem
          item={{ icon: Settings, label: 'Settings', path: '/settings' }}
          isCollapsed={isCollapsed}
          onClick={onNavigate}
        />
      </div>
    </div>
  );
};

// ── Notification dropdown ─────────────────────────────────────────────────────

const NotificationMenu = ({ pendingCount }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="relative flex items-center justify-center transition-colors rounded-xl"
        style={{
          width: 36, height: 36,
          color: SYSTEM_COLORS.textMuted,
          backgroundColor: open ? SYSTEM_COLORS.shellSurfaceMuted : 'transparent',
        }}
      >
        <Bell size={17} />
        {pendingCount > 0 && (
          <span
            className="absolute border-2 border-white rounded-full"
            style={{
              top: 8, right: 8,
              width: 7, height: 7,
              backgroundColor: SYSTEM_COLORS.danger,
            }}
          />
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 overflow-hidden border top-full rounded-2xl"
          style={{
            width: 'min(320px, calc(100vw - 1.5rem))',
            backgroundColor: SYSTEM_COLORS.shellSurface,
            borderColor: SYSTEM_COLORS.borderSoft,
            boxShadow: SYSTEM_COLORS.shadowStrong,
            zIndex: 200,
          }}
        >
          <div className="px-4 py-2.5 border-b" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
              Notifications
            </p>
          </div>
          <div className="p-2.5">
            {pendingCount > 0 ? (
              <Link
                to="/user-management?tab=approvals"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 rounded-xl p-2.5 transition-colors hover:bg-gray-50"
              >
                <div
                  className="flex items-center justify-center flex-shrink-0 rounded-xl"
                  style={{ width: 36, height: 36, backgroundColor: SYSTEM_COLORS.primaryTint }}
                >
                  <UserCheck size={15} style={{ color: BRAND_COLORS.primary }} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {pendingCount} pending approval{pendingCount !== 1 ? 's' : ''}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">Review member onboarding requests</p>
                </div>
              </Link>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-5">
                <div
                  className="flex items-center justify-center rounded-2xl"
                  style={{ width: 38, height: 38, backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}
                >
                  <Bell size={16} className="text-gray-300" />
                </div>
                <p className="text-sm text-gray-400">You're all caught up</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── User menu ─────────────────────────────────────────────────────────────────

const UserMenu = ({ user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const avatarSrc = user?.avatar || user?.avatar_url || user?.profile_image || user?.photo_url;

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const Avatar = ({ size = 35 }) => (
    <div
      className="flex items-center justify-center flex-shrink-0 overflow-hidden font-bold text-white rounded-full"
      style={{ width: size, height: size, fontSize: size * 0.38, backgroundColor: BRAND_COLORS.primary }}
    >
      {avatarSrc && !avatarError
        ? <img src={avatarSrc} alt="" className="object-cover w-full h-full" onError={() => setAvatarError(true)} />
        : getInitials(user?.name)}
    </div>
  );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 transition-all rounded-xl"
        style={{
          padding: '5px 8px 5px 5px',
          backgroundColor: open ? SYSTEM_COLORS.shellSurfaceMuted : 'transparent',
          border: `1px solid ${open ? SYSTEM_COLORS.borderSoft : 'transparent'}`,
        }}
      >
        <Avatar size={28} />
        <div className="hidden min-w-0 text-left sm:block">
          <p className="text-[12px] font-semibold text-gray-800 leading-tight truncate max-w-[90px]">
            {user?.name || 'User'}
          </p>
          <p className="text-[10px] text-gray-400 leading-tight truncate max-w-[90px]">
            {getRoleLabel(user?.role)}
          </p>
        </div>
        <ChevronDown size={11} className="flex-shrink-0 hidden text-gray-400 sm:block" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 overflow-hidden border top-full rounded-xl"
          style={{
            width: 208,
            backgroundColor: SYSTEM_COLORS.shellSurface,
            borderColor: SYSTEM_COLORS.borderSoft,
            boxShadow: SYSTEM_COLORS.shadowStrong,
            zIndex: 200,
          }}
        >
          {/* Identity card */}
          <div className="flex items-center gap-2.5 px-3 py-2.5 border-b"
            style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
            <Avatar size={34} />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-gray-800 truncate">{user?.name || 'User'}</p>
              <p className="text-[11px] text-gray-400 truncate">{user?.email || getRoleLabel(user?.role)}</p>
            </div>
          </div>
          {/* Actions */}
          <div className="p-1.5 space-y-0.5">
            <button
              onClick={() => { setOpen(false); navigate('/settings'); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-gray-700 text-left transition-colors hover:bg-gray-50"
            >
              <Settings size={13} style={{ color: BRAND_COLORS.primary }} />
              Account Settings
            </button>
            <button
              onClick={() => { setOpen(false); onLogout(); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-[13px] text-left transition-colors hover:bg-red-50"
              style={{ color: SYSTEM_COLORS.danger }}
            >
              <LogOut size={13} />
              Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Topbar ────────────────────────────────────────────────────────────────────

const Topbar = ({ onMobileMenuOpen }) => {
  const { pathname } = useLocation();
  const { user, logout } = useApiAuth();
  const navigate = useNavigate();
  const [pendingCount] = useState(0);

  const handleLogout = async () => {
    try {
      navigate('/login', { state: { isLogout: true }, replace: true });
      await logout();
      toast.success('Signed out successfully');
    } catch {
      toast.error('Failed to sign out');
    }
  };

  return (
    <div
      className="flex items-center flex-shrink-0 border-b"
      style={{
        height: 56,
        padding: '0 12px 0 12px',
        backgroundColor: 'rgba(255,255,255,0.97)',
        borderColor: SYSTEM_COLORS.borderSoft,
      }}
    >
      {/* Left — mobile menu + page title */}
      <div className="flex items-center flex-1 min-w-0 gap-2">
        <button
          onClick={onMobileMenuOpen}
          className="flex items-center justify-center flex-shrink-0 transition-colors lg:hidden rounded-xl"
          style={{
            width: 34, height: 34,
            backgroundColor: SYSTEM_COLORS.shellSurfaceMuted,
            color: SYSTEM_COLORS.textMuted,
          }}
        >
          <Menu size={16} />
        </button>
        <h1 className="text-[14px] sm:text-[15px] font-semibold text-gray-800 truncate">
          {getPageTitle(pathname)}
        </h1>
      </div>

      {/* Right — bell + divider + user */}
      <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
        <NotificationMenu pendingCount={pendingCount} />
        <div
          className="hidden mx-1 sm:block"
          style={{ width: 1, height: 18, backgroundColor: SYSTEM_COLORS.borderSoft }}
        />
        <UserMenu user={user} onLogout={handleLogout} />
      </div>
    </div>
  );
};

// ── Layout root ───────────────────────────────────────────────────────────────

const Layout = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen]   = useState(false);
  const { loading } = useApiAuth();
  const location = useLocation();
  const { logPageVisit } = useActivityLog();

  // Auto-collapse on smaller desktops; respect manual toggles after mount
  useEffect(() => {
    if (window.innerWidth < AUTO_COLLAPSE_BELOW) setIsCollapsed(true);
  }, []);

  // Re-collapse automatically if window resizes down below threshold
  const handleResize = useCallback(() => {
    if (window.innerWidth < 1024) {
      setIsMobileOpen(false); // close any stray mobile drawer
    }
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  useEffect(() => { logPageVisit(location.pathname); }, [location.pathname, logPageVisit]);
  useEffect(() => { setIsMobileOpen(false); }, [location.pathname]);

  const sidebarW = isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  if (loading) {
    return (
      <div
        className="flex items-center justify-center min-h-screen"
        style={{ backgroundColor: SYSTEM_COLORS.shellBackground }}
      >
        <div
          className="border-b-2 rounded-full animate-spin"
          style={{ width: 28, height: 28, borderColor: BRAND_COLORS.primary }}
        />
      </div>
    );
  }

  return (
    <>
      <Helmet><title>{getPageTitle(location.pathname)} | A.M.A.R.M.S</title></Helmet>

      <div
        className="flex h-screen overflow-hidden"
        style={{ backgroundColor: SYSTEM_COLORS.shellBackground }}
      >
        {/* ── Desktop sidebar ──────────────────────────────────────────── */}
        <aside
          className="relative flex-col flex-shrink-0 hidden h-full transition-all duration-300 lg:flex"
          style={{ width: sidebarW, backgroundColor: SYSTEM_COLORS.navSurface }}
        >
          <Sidebar isCollapsed={isCollapsed} onNavigate={() => {}} />

          {/* Floating collapse toggle */}
          <button
            onClick={() => setIsCollapsed(v => !v)}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="absolute z-50 flex items-center justify-center transition-all duration-150 border rounded-md hover:scale-110 active:scale-95"
            style={{
              width: 30, height: 30,
              top: 66, right: -14,
              backgroundColor: SYSTEM_COLORS.shellSurface,
              borderColor: SYSTEM_COLORS.borderSoft,
              boxShadow: SYSTEM_COLORS.shadowSoft,
              color: BRAND_COLORS.primary,
            }}
          >
            {isCollapsed
              ? <ChevronRight size={20} />
              : <ChevronLeft  size={20} />}
          </button>
        </aside>

        {/* ── Content column ───────────────────────────────────────────── */}
        <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
          <Topbar onMobileMenuOpen={() => setIsMobileOpen(true)} />

          <main
            className="flex-1 overflow-y-auto"
            style={{
              backgroundImage: `linear-gradient(rgba(244,247,251,0.90
              ), rgba(244,247,251,0.90)), url(${backgroundImg})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundAttachment: 'scroll',
            }}
          >
            <div className="p-4 sm:p-5 lg:p-6 xl:p-7 2xl:p-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>

      {/* ── Mobile sidebar overlay ───────────────────────────────────── */}
      {isMobileOpen && (
        <div className="fixed inset-0 z-[200] lg:hidden">
          {/* Backdrop */}
          <button
            className="absolute inset-0 w-full h-full"
            style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}
            onClick={() => setIsMobileOpen(false)}
          />
          {/* Drawer — responsive width: 280px or 82 vw, whichever is smaller */}
          <aside
            className="absolute inset-y-0 left-0 flex flex-col"
            style={{
              width: 'min(280px, 82vw)',
              backgroundColor: SYSTEM_COLORS.navSurface,
              zIndex: 201,
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setIsMobileOpen(false)}
              className="absolute flex items-center justify-center rounded-xl"
              style={{
                top: 12, right: 10,
                width: 30, height: 30,
                backgroundColor: 'rgba(255,255,255,0.12)',
                color: 'rgba(255,255,255,0.85)',
              }}
            >
              <X size={14} />
            </button>

            <Sidebar
              isCollapsed={false}
              onNavigate={() => setIsMobileOpen(false)}
            />
          </aside>
        </div>
      )}
    </>
  );
};

export default Layout;
